import { useLingui } from "@lingui/react/macro";
import { useTenantList } from "@/hooks/useTenantLists";

/**
 * What kind of thing a container or a tool is.
 *
 * Both fields were free text, and free text is why they meant nothing — "sub
 * type… I don't know what that is" (T-1005, transcript 47:49). One kitchen typed
 * "deli", the next "Deli Cup", and nothing could be filtered or counted. These
 * are the shapes a prep kitchen actually stocks.
 *
 * Since T-1006 the lists belong to the tenant and these are the seed the API
 * hands a kitchen that has never opened Configuration. They stay here as well so
 * a picker still has options while that request is in flight.
 */
export const CONTAINER_SUB_TYPES = [
  "GN Pan",
  "Deli Cup",
  "Cambro",
  "Sheet Pan",
  "Bain Marie",
  "Squeeze Bottle",
  "Vacuum Bag",
  "Bus Tub",
] as const;

export const TOOL_TYPES = [
  "Pot",
  "Pan",
  "Knife",
  "Cutting Board",
  "Blender",
  "Mixer",
  "Scale",
  "Thermometer",
  "Utensil",
] as const;

/**
 * The options to offer, given what is stored.
 *
 * Anything typed before the list existed is appended rather than dropped: a
 * select that cannot show its own value rewrites it on the next save, and the
 * kitchen loses the one word it had bothered to write down.
 */
function withCurrent(catalog: readonly string[], current?: string | null): string[] {
  const options = [...catalog];
  if (current && !options.includes(current)) options.push(current);
  return options;
}

export function containerSubTypeOptions(
  current?: string | null,
  catalog?: readonly string[],
): string[] {
  return withCurrent(catalog ?? CONTAINER_SUB_TYPES, current);
}

export function toolTypeOptions(current?: string | null, catalog?: readonly string[]): string[] {
  return withCurrent(catalog ?? TOOL_TYPES, current);
}

/** The tenant's container shapes, falling back to the seed while they load. */
export function useContainerSubTypes(): readonly string[] {
  return useTenantList("containerSubTypes") ?? CONTAINER_SUB_TYPES;
}

/** The tenant's tool kinds, falling back to the seed while they load. */
export function useToolTypes(): readonly string[] {
  return useTenantList("toolTypes") ?? TOOL_TYPES;
}

export function useContainerSubTypeNames(): Record<string, string> {
  const { t } = useLingui();
  return {
    "GN Pan": t`GN Pan`,
    "Deli Cup": t`Deli Cup`,
    Cambro: t`Cambro`,
    "Sheet Pan": t`Sheet Pan`,
    "Bain Marie": t`Bain Marie`,
    "Squeeze Bottle": t`Squeeze Bottle`,
    "Vacuum Bag": t`Vacuum Bag`,
    "Bus Tub": t`Bus Tub`,
  };
}

export function useToolTypeNames(): Record<string, string> {
  const { t } = useLingui();
  return {
    Pot: t`Pot`,
    Pan: t`Pan`,
    Knife: t`Knife`,
    "Cutting Board": t`Cutting Board`,
    Blender: t`Blender`,
    Mixer: t`Mixer`,
    Scale: t`Scale`,
    Thermometer: t`Thermometer`,
    Utensil: t`Utensil`,
  };
}
